import React, { Fragment } from 'react'
import styled from 'styled-components'
import {
  useExpanded,
  usePagination,
  useRowSelect,
  useTable,
} from 'react-table'
import TableRow from './TableRow'
import ContextMenu from '../UI/ContextMenu'
import AlertLabel from '../UI/AlertLabel'
import useWindowSize from '../../hooks/useWindowSize'
import SearchBar from '../UI/SearchBar'
import { prepareFilters } from '../../utils/prepareFilters'
import { filtersOptions } from '../../utils/filtersOptions'
import { TableProps } from '../../types/TableProps'

const SearchBarWrapper = styled.div`
  position: relative;
  padding-inline: 20px;
  z-index: 10;
`

const TableStyle = styled.div<{ height?: string }>`
  padding-inline: 20px 10px;
  padding-top: 20px;
  overflow: auto;
  height: ${({ height }) => height};
  position: relative;

  table {
    margin-top: -20px;
    width: 100%;
    font-size: 12px;
    border-collapse: separate;
    border-spacing: 0 5px;

    th,
    td {
      display: table-cell;
      border: transparent;
      margin: 0;
      padding: 1rem;

      :last-child {
        border-right: 0;
      }
    }

    th {
      font-weight: normal;
    }
  }

  thead {
    th {
      padding-block: 8px;
    }
  }

  tbody {
    tr {
      display: table-row;
      vertical-align: inherit;
      border-color: inherit;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.07);
      border-radius: 5px;
    }
  }

  td {
    padding: 5px;
    background-color: #ffffff;
  }

  td:first-child {
    border-top-left-radius: 5px;
    border-bottom-left-radius: 5px;
  }

  td:last-child {
    border-bottom-right-radius: 5px;
    border-top-right-radius: 5px;
    padding: 0;
  }
  th:last-child {
    padding-left: 0;
  }

  .pagination {
    padding: 0.5rem;
  }
`

const IndeterminateCheckbox = React.forwardRef(
  ({ indeterminate, ...rest }: any, ref) => {
    const defaultRef = React.useRef()
    const resolvedRef: any = ref || defaultRef

    React.useEffect(() => {
      resolvedRef.current.indeterminate = indeterminate
    }, [resolvedRef, indeterminate])

    return (
      <Fragment>
        <input type="checkbox" ref={resolvedRef} {...rest} />
      </Fragment>
    )
  }
)

IndeterminateCheckbox.displayName = 'IndeterminateCheckbox'

function TableFunc({ columns, data, isMultiSelect }: any) {
  // Use the state and functions returned from useTable to build your UI
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,

    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    visibleColumns,
    // totalColumnsWidth,
    // selectedFlatRows,
    state: {
      pageIndex,
      pageSize,
      //selectedRowIds
    },
  }: any = useTable(
    {
      columns,
      data,
    },
    useExpanded,
    usePagination,
    useRowSelect,

    (hooks) => {
      hooks.visibleColumns.push((columns) => [
        // Let's make a column for selection
        {
          accessor: 'alertLabel',
          style: {
            padding: '0 10px 0 0',
          },
          Header: () => null,
          Cell: function label(value) {
            return <AlertLabel {...value.row.original} />
          },
        },
        {
          id: 'selection',
          style: {
            display: `${!isMultiSelect && 'none'}`,
          },
          // The header can use the table's getToggleAllRowsSelectedProps method
          // to render a checkbox
          Header: function Header({
            getToggleAllPageRowsSelectedProps,
          }: any) {
            return (
              <IndeterminateCheckbox
                {...getToggleAllPageRowsSelectedProps()}
              />
            )
          },

          // The cell can use the individual row's getToggleRowSelectedProps method
          // to the render a checkbox
          Cell: function Cell({ row }: any) {
            return (
              <div>
                <IndeterminateCheckbox
                  {...row.getToggleRowSelectedProps()}
                />
              </div>
            )
          },
        },

        ...columns,
      ])
    }
  )

  // Render the UI for your table
  return (
    <Fragment>
      <table {...getTableProps()}>
        <thead>
          {headerGroups.map((headerGroup: any, key) => (
            <tr key={key} {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column: any, key) => (
                <th
                  key={key}
                  {...column.getHeaderProps([
                    {
                      className: column.className,
                      style: column.style,
                    },
                  ])}
                >
                  {column.render('Header')}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {page.map((row, key) => {
            prepareRow(row)
            return (
              <TableRow
                visibleColumns={visibleColumns}
                key={key}
                rowData={row}
                id={key}
              />
            )
          })}
        </tbody>
      </table>
      {/*
        Pagination can be built however you'd like.
        This is just a very basic UI implementation:
      */}
      <div className="pagination">
        <button onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
          {'<<'}
        </button>{' '}
        <button onClick={() => previousPage()} disabled={!canPreviousPage}>
          {'<'}
        </button>{' '}
        <button onClick={() => nextPage()} disabled={!canNextPage}>
          {'>'}
        </button>{' '}
        <button
          onClick={() => gotoPage(pageCount - 1)}
          disabled={!canNextPage}
        >
          {'>'}
          {'>>'}
        </button>{' '}
        <span>
          Page{' '}
          <strong>
            {pageIndex + 1} of {pageOptions.length}
          </strong>{' '}
        </span>
        <span>
          | Go to page:{' '}
          <input
            type="number"
            defaultValue={pageIndex + 1}
            onChange={(e) => {
              const page = e.target.value ? Number(e.target.value) - 1 : 0
              gotoPage(page)
            }}
            style={{ width: '100px' }}
          />
        </span>{' '}
        <select
          value={pageSize}
          onChange={(e) => {
            setPageSize(Number(e.target.value))
          }}
        >
          {[10, 20, 30, 40, 50].map((pageSize) => (
            <option key={pageSize} value={pageSize}>
              Show {pageSize}
            </option>
          ))}
        </select>
      </div>
    </Fragment>
  )
}

export type TablePaginationProps = TableProps

export const TablePagination: React.FC<TablePaginationProps> = ({
  onFilter,
  actions,
  onAction,
  isMultiSelect = false,
  isExpandable,
  styles,
  height100,
  data,
}) => {
  const { height } = useWindowSize()
  const tableHeight = height100 ? '100%' : `${height - 373}px`
  const filters = prepareFilters(data.metadata.columns)
  const filterOptions = filtersOptions(filters, data.data)

  const columns = React.useMemo(
    () => [
      {
        id: 'expander',
        style: {
          padding: 0,
        },
        Header: () => null,
        Cell: function expandSubComponent({ row }) {
          return isExpandable ? (
            <span {...row.getToggleRowExpandedProps()}>
              {row.isExpanded ? '▼' : '►'}
            </span>
          ) : null
        },
      },
      ...data.metadata.columns,
      // {
      //   Header: 'ID',
      //   accessor: 'id',
      //   Cell: function link({ value }) {
      //     return <Link to={`/incidents/${value}/main`}>{value}</Link>
      //   },
      // },
    ],
    []
  )

  actions &&
    onAction &&
    columns.push({
      id: 'contextMenu',
      Header: () => null,
      Cell: function contextMenu({ row }) {
        return (
          <ContextMenu
            actions={actions}
            onAction={onAction}
            id={row.values.id}
          />
        )
      },
    })

  return (
    <Fragment>
      <SearchBarWrapper style={styles?.searchBarStyles}>
        <SearchBar
          onFilter={onFilter}
          filtersSet={filters}
          filterOptions={filterOptions}
        />
      </SearchBarWrapper>
      <TableStyle height={tableHeight} style={styles?.tablesStyles}>
        <TableFunc
          isMultiSelect={isMultiSelect}
          columns={columns}
          data={data.data}
        />
      </TableStyle>
    </Fragment>
  )
}

export default TablePagination
